import type { Feature, FeatureCollection, Point } from "geojson"
import {
  findCurrentTarget,
  type PastRouteStop,
  type Route2g,
} from "@/reducers/routes2gSlice"
import { parseWarsawTime } from "@/lib/warsawTime"

/** Arrival is detected on the approach and GPS pings come every few minutes, so
 * the ping at the stop itself lands after the arrival time (3-6 min on prod). */
const TRAIL_END_AFTER_ARRIVAL_MS = 10 * 60 * 1000

export type CurrentOrderPastStops = {
  /** Epoch ms of the current delivery order, as a string. */
  orderTimestamp: string
  /** In the order the driver got through them, without passthroughs. */
  stops: PastRouteStop[]
  /** ISO time the driven trail ends at: just after the arrival at the last stop
   * of a closed order. Undefined while the order runs, and when the last stop
   * has no arrival (skipped), so the drive past that stop stays visible as
   * proof. */
  trailEndsAt?: string
}

/** MapLibre feature properties, so every field is a string ("" when missing). */
export type PastStopFeatureProps = {
  id: string
  status: "left" | "skipped"
  action: string
  address: string
  ref: string
  arrivedAt: string
}

const largestOrderTimestamp = (
  timestamps: (string | undefined)[],
): string | undefined => {
  let best: string | undefined
  let bestValue = -Infinity
  for (const timestamp of timestamps) {
    if (timestamp === undefined) continue
    const value = Number(timestamp)
    if (!Number.isFinite(value)) continue
    if (value > bestValue) {
      best = timestamp
      bestValue = value
    }
  }
  return best
}

const isCountable = (stop: Pick<PastRouteStop, "action" | "status">): boolean =>
  stop.status !== undefined && stop.action !== "passthrough"

/**
 * The stops of the driver's current delivery order that they have already
 * left or skipped. The current order is the one of the current target; without
 * a target (or one with no order) it is the newest order known.
 */
export function selectCurrentOrderPastStops(
  route: Route2g | undefined,
): CurrentOrderPastStops | null {
  if (!route) return null
  const stops = route.stops ?? []
  const pastStops = route.pastStops ?? []

  const target = findCurrentTarget(stops)
  const orderTimestamp =
    target?.order_timestamp ??
    largestOrderTimestamp([
      ...stops.map((stop) => stop.order_timestamp),
      ...pastStops.map((stop) => stop.order_timestamp),
    ])
  if (orderTimestamp === undefined) return null

  const firstOfOrder = pastStops.findIndex(
    (stop) => stop.order_timestamp === orderTimestamp,
  )
  const result: PastRouteStop[] =
    firstOfOrder === -1 ? [] : pastStops.slice(firstOfOrder).filter(isCountable)

  const seen = new Set(result.map((stop) => stop.id))
  for (const stop of stops) {
    if (
      stop.id === undefined ||
      seen.has(stop.id) ||
      stop.order_timestamp !== orderTimestamp ||
      (stop.proximity_status !== "left" && stop.proximity_status !== "skipped")
    ) {
      continue
    }
    const pastStop: PastRouteStop = {
      id: stop.id,
      location: stop.location,
      action: stop.action,
      ref: stop.ref,
      status: stop.proximity_status,
      arrived_at: stop.arrived_at,
      order_timestamp: stop.order_timestamp,
    }
    if (!isCountable(pastStop)) continue
    seen.add(stop.id)
    result.push(pastStop)
  }

  const lastArrival = target ? null : parseWarsawTime(result.at(-1)?.arrived_at)
  return {
    orderTimestamp,
    stops: result,
    ...(lastArrival !== null && {
      trailEndsAt: new Date(
        lastArrival + TRAIL_END_AFTER_ARRIVAL_MS,
      ).toISOString(),
    }),
  }
}

/** Stops without usable coordinates are left out: MapLibre cannot draw them. */
export function pastStopsFeatureCollection(
  stops: PastRouteStop[],
): FeatureCollection<Point, PastStopFeatureProps> {
  const features: Feature<Point, PastStopFeatureProps>[] = []
  for (const stop of stops) {
    const lon = parseFloat(stop.location.lon ?? "")
    const lat = parseFloat(stop.location.lat ?? "")
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) continue
    features.push({
      type: "Feature",
      geometry: { type: "Point", coordinates: [lon, lat] },
      properties: {
        id: stop.id,
        status: stop.status ?? "left",
        action: stop.action ?? "",
        address: stop.location.formattedAddress ?? "",
        ref: stop.ref ?? "",
        arrivedAt: stop.arrived_at ?? "",
      },
    })
  }
  return { type: "FeatureCollection", features }
}
